import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../../common/types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { platformMeta, platformSolidFill } from '../../platformMeta';
import { SerializedSocialChannel, SocialPlatform } from '../../types';
import { canChannelTakeReel, channelNeedsSignIn } from './reelReview';
import { reelApproveMessages } from './reelReviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelChannelChipsProps {
  channels: SerializedSocialChannel[];
  picked: SocialPlatform[];
  onToggle: (platform: SocialPlatform) => void;
}

/**
 * The channel chips of the approve sheet (design d1). A healthy channel toggles. A channel that
 * needs a new sign-in shows the red tag, cannot be picked and opens Channels.
 */
const ReelChannelChipsComponent: React.FC<ReelChannelChipsProps> = ({ channels, picked, onToggle }) => {
  const intl = useIntl();
  const router = useSMLRouter();

  const chips = useMemo(
    () =>
      channels.map((channel) => {
        const meta = platformMeta[channel.platform];
        const mark = (
          <span className={styles.cmark} style={{ background: platformSolidFill(channel.platform) }}>
            <FontAwesomeIcon icon={meta.icon} />
          </span>
        );
        if (channelNeedsSignIn(channel)) {
          return (
            <button
              key={channel.platform}
              type="button"
              className={clsx(styles.cchip, styles.cchipWarn)}
              onClick={() => router.push(SMLRoutes.promoteChannels)}
            >
              {mark}
              {meta.name}
              <span className={styles.need}>{intl.formatMessage(reelApproveMessages.signInAgain)}</span>
            </button>
          );
        }
        if (!canChannelTakeReel(channel)) {
          return null;
        }
        const isOn = picked.includes(channel.platform);
        return (
          <button
            key={channel.platform}
            type="button"
            role="checkbox"
            aria-checked={isOn}
            className={clsx(styles.cchip, isOn && styles.cchipOn)}
            onClick={() => onToggle(channel.platform)}
          >
            <span className={styles.tick}>{isOn && <FontAwesomeIcon icon={faCheck} />}</span>
            {mark}
            {meta.name}
          </button>
        );
      }),
    [channels, intl, onToggle, picked, router]
  );

  return <div className={styles.chips}>{chips}</div>;
};

export const ReelChannelChips = TypedMemo(ReelChannelChipsComponent);
